import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import {
    EyeIcon,
    EyeSlashIcon,
    CheckCircleIcon,
    XCircleIcon,
    TrashIcon,
    PencilIcon,
} from "@heroicons/react/24/outline";
import ApiService from "../../models/ApiService";

const AddUser = ({ onSubmit }) => {
    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm();
    const [showPassword, setShowPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState("");
    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(true);

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const res = await ApiService.getAllAdminUsers();
            setUsers(res.data.responseContent || []);
        } catch (error) {
            console.error("Error fetching users:", error);
            setSubmitError("Failed to load users. Please try again.");
        } finally {
            setLoadingUsers(false);
        }
    };

    const onFormSubmit = async (data) => {
        setIsSubmitting(true);
        setSubmitSuccess(false);
        setSubmitError("");

        try {
            const res = await ApiService.CreateUser(data);
            console.log(res);

            setSubmitSuccess(true);
            reset();
            fetchUsers(); // Refresh the user list after creation

            // Auto-hide success message after 3s
            setTimeout(() => {
                setSubmitSuccess(false);
            }, 3000);
        } catch (error) {
            console.error("Error creating user:", error);
            setSubmitError(
                error.message || "Failed to create user. Please try again."
            );
        } finally {
            setIsSubmitting(false);
        }
    };
  


    return (
        <div className="w-full p-6 bg-white rounded-lg shadow-md">
            <h2 className="text-2xl font-bold text-[--text-color-g] mb-6">
                User Management
            </h2>

            {/* Add User Form */}
            <div className="mb-8">
                <h3 className="text-xl font-semibold text-[--text-color-g] mb-4">
                    Add New User
                </h3>
                <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
                    {/* Username */}
                    <div>
                        <label
                            htmlFor="username"
                            className="block font-medium text-black mb-1"
                        >
                            Username
                        </label>
                        <input
                            type="text"
                            id="username"
                            {...register("username", {
                                required: "Username is required",
                                minLength: {
                                    value: 4,
                                    message: "Username must be at least 4 characters",
                                },
                            })}
                            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-[--border-color-g] ${errors.username ? "border-red-500" : "border-gray-300"
                                }`}
                        />
                        {errors.username && (
                            <p className="mt-1 text-sm text-red-600">
                                {errors.username.message}
                            </p>
                        )}
                    </div>

                    {/* Password */}
                    <div>
                        <label
                            htmlFor="password"
                            className="block font-medium text-black mb-1"
                        >
                            Password
                        </label>
                        <div className="relative">
                            <input
                                type={showPassword ? "text" : "password"}
                                id="password"
                                {...register("password", {
                                    required: "Password is required",
                                    minLength: {
                                        value: 4,
                                        message: "Password must be at least 4 characters",
                                    },
                                })}

                                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-[--border-color-g] ${errors.password ? "border-red-500" : "border-gray-300"
                                    }`}
                            />
                            <button
                                type="button"
                                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? (
                                    <EyeSlashIcon className="h-5 w-5 text-[--text-color]" />
                                ) : (
                                    <EyeIcon className="h-5 w-5 text-[--text-color]" />
                                )}
                            </button>
                        </div>
                        {errors.password && (
                            <p className="mt-1 text-sm text-red-600">
                                {errors.password.message}
                            </p>
                        )}

                    </div>

                    {/* Submit Button and Status */}
                    <div className="pt-4">
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[--primary-btn-color] hover:bg-[--secondary-btn-color] ${isSubmitting ? "opacity-75 cursor-not-allowed" : ""
                                }`}
                        >
                            {isSubmitting ? "Creating User..." : "Create User"}
                        </button>

                        {submitSuccess && (
                            <div className="mt-4 p-3 bg-green-50 text-green-700 rounded-md flex items-center">
                                <CheckCircleIcon className="h-5 w-5 mr-2" />
                                User created successfully!
                            </div>
                        )}

                        {submitError && (
                            <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-md flex items-center">
                                <XCircleIcon className="h-5 w-5 mr-2" />
                                {submitError}
                            </div>
                        )}
                    </div>
                </form>
            </div>

            {/* Users List */}
            <div>
                <h3 className="text-xl font-semibold text-[--text-color-g] mb-4">
                    Existing Users
                </h3>
                {loadingUsers ? (
                    <div className="flex justify-center items-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                    </div>
                ) : users.length === 0 ? (
                    <div className="text-center py-4 text-gray-500">No users found</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th
                                        scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        Username
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        Created At
                                    </th>
                                    {/* <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Actions
                  </th> */}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {users.map((user) => (
                                    <tr key={user.id}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {user.username}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {new Date(user.createdAt).toLocaleString()}
                                        </td>
                                        {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex space-x-2">
                        <button className="text-blue-600 hover:text-blue-900">
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          className="text-red-600 hover:text-red-900"
                          onClick={() => handleDeleteUser(user.id)}
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td> */}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AddUser;
